import { Injectable, Injector } from '@angular/core';
import { globalUtilsHelper } from '@common-modules/shared/helpers/global-utils-helper';
import { LocalizationHelperService } from '@common-modules/shared/localization/localization-helper.service';
import { UserActivitityTrendEnum } from '@common-modules/shared/model/roles/user-activity.enum';
import { DimensionTypesEnum } from '@common-modules/shared/model/shared/dimension-types';
import { ChartSerieTypeEnum } from '@common-modules/wlm-charts/core/models/chart-serie-type.enum';
import { GCartesianChartSerie } from '@common-modules/wlm-charts/core/models/generic-chart-settings/g-cartesian-chart-series';
import { GChartGrid } from '@common-modules/wlm-charts/core/models/generic-chart-settings/g-chart-grid';
import { GChartSerieDataPoint } from '@common-modules/wlm-charts/core/models/generic-chart-settings/g-chart-serie-data-point';
import { GenericCartesianChartSettings } from '@common-modules/wlm-charts/core/models/generic-chart-settings/generic-cartesian-chart-settings';
import { BaseCartesianChartService } from '@common-modules/wlm-charts/core/services/base-cartesian-chart.service';
import { asEnumerable } from 'linq-es2015';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ChartAttributes, GridAttributeValue } from '../models/grid-attribute-value';
import { ScenarioInfoService } from '../services/scenario-info.service';

@Injectable()
export class AttributesChartService extends BaseCartesianChartService {
  protected NADimension = 50;
  private readonly _basicChartPadding = '24px';
  private readonly _adjustLegendChartPadding = '70x';

  constructor(
    injector: Injector,
    private _localization: LocalizationHelperService,
    private _scenarioInfoService: ScenarioInfoService
  ) {
    super(injector);
  }

  protected get url() {
    return `${this.apiUrl}/attributes`;
  }

  public getData(params: any): Observable<any[]> {
    const queryParams = params.queryParams;

    const category = queryParams.category;

    const chartParams = {
      snapshotId: queryParams.snapshotId,
      networkElementId: queryParams.networkElementId,
      scenarioId: queryParams.scenarioId,
      startDate: new Date(params.startDate),
      endDate: new Date(params.endDate),
      category: queryParams.category.name,
      attributeNames: queryParams.attributeNames,
    };

    return this._scenarioInfoService.getLayerAttributesForChart(chartParams).pipe(
      map((attributes) => {
        const attributesNames = asEnumerable(attributes)
          .Select((x) => x.name)
          .Distinct()
          .ToArray();

        const attributesValues: ChartAttributes[] = [];
        attributesNames.forEach((key) => {
          const attributesByKey = attributes.filter((x) => x.name == key);
          const attrInfo = category.attributes.find((x) => x.name == key);
          const attributeGridValues = attributesByKey.map((attr) => {
            const attributeValue = new GridAttributeValue({ ...attrInfo, ...attr });
            const { value, format, units } = attributeValue;
            attributeValue.formattedValue = this._localization.formatAttributeValue(
              value,
              format,
              units
            );
            return attributeValue;
          });

          attributesValues.push(new ChartAttributes({ name: key, values: attributeGridValues }));
        });

        return attributesValues;
      })
    );
  }

  public mapDataToGenericSettings(
    data: ChartAttributes[]
  ): Observable<GenericCartesianChartSettings> {
    const series = data.map((attrValues) => {
      const dataPoints: GChartSerieDataPoint[] = [];

      for (const trendValue of attrValues.values) {
        const dataPoint: GChartSerieDataPoint = {
          pointCategory: new Date(trendValue.resultDateTime),
          pointValue: globalUtilsHelper.commaNumericStringToNumber(trendValue.value),
        };
        dataPoints.push(dataPoint);
      }

      const unit = attrValues.values[0].units;

      const cartesianSerie: GCartesianChartSerie = {
        name: attrValues.name,
        type: ChartSerieTypeEnum.Line,
        xAxisIndex: 0,
        yAxisIndex: 0,
        xAxisWLMDimensionTypeId: DimensionTypesEnum.NA,
        yAxisWLMDimensionTypeId: DimensionTypesEnum.NA,
        yAxisWLMUnitTypeIdTo: this.NADimension,
        yAxisWLMUnitTypeIdFrom: this.NADimension,
        yAxisWLMUnitCustomLabel: unit,
        yAxisName: unit,
        dataPoints,
      };
      return cartesianSerie;
    });

    const chartSettings = new GenericCartesianChartSettings({
      series,
      grid: new GChartGrid({
        top: this._basicChartPadding,
        right: this._basicChartPadding,
        left: this._basicChartPadding,
        bottom: this._adjustLegendChartPadding,
      }),
    });

    return of(chartSettings);
  }

  getSerieName(userActivity: UserActivitityTrendEnum, serieLabels: { [key: string]: string }) {
    return serieLabels[UserActivitityTrendEnum[userActivity]];
  }
}
